/**
 * UiTreeViewer.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Renders the live Application UI Element Tree received from the Angular app.
 *
 * Features:
 *   • Hierarchical tree built from flat UiElement[] parent/child refs
 *   • Collapsible nodes
 *   • Element type icons
 *   • Route grouping
 *   • Click to select; selection shows full GUID + Copy + Highlight
 *   • Tree text export
 *   • Search/filter (matches against BOTH label and id, so support agents
 *     can find an element by its human-readable name without needing the GUID)
 *
 * GUID display
 *   The element id is shown as small dimmed metadata next to the label.
 *   With v4 GUIDs in production these will look like
 *     "a7c3e1b4-5d8f-4e2a-bc9d-1f4e7a8b3c6d"
 *   which is unreadable to humans but uniquely identifies the element to the
 *   Angular app. The human-readable label (e.g. "New Shipment") is the
 *   primary display.
 */
import React, { useState, useMemo } from 'react';
import { UiElement, UiTreeNode } from '../../types';
import { isMainSection, filterTreeToMainSections } from '../../services/nodeFilter';
import s from './UiTreeViewer.module.css';

// ── Build tree ────────────────────────────────────────────────────────────────
function buildTree(elements: UiElement[]): UiTreeNode[] {
  const map = new Map<string, UiTreeNode>();
  const roots: UiTreeNode[] = [];

  elements.forEach(el => { map.set(el.id, { ...el, children: [] }); });

  elements.forEach(el => {
    const node = map.get(el.id)!;
    if (el.parent && map.has(el.parent)) {
      map.get(el.parent)!.children.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
}

// ── Element type icons ────────────────────────────────────────────────────────
function typeIcon(type?: string, label?: string, id?: string): string {
  const t = (type  ?? '').toLowerCase();
  const l = (label ?? '').toLowerCase();
  // For GUID-based ids we cannot infer anything from the id string itself,
  // so we look at the label too.
  if (t === 'button' || l.includes('button') || l.includes('btn')) return '🔘';
  if (t === 'input'  || l.includes('input'))                       return '✏️';
  if (t === 'select' || l.includes('dropdown'))                    return '📋';
  if (t === 'a'      || t === 'nav-item' || l.includes('menu'))    return '🔗';
  if (l.includes('grid') || l.includes('table'))                   return '📊';
  if (l.includes('row'))                                           return '📂';
  if (l.includes('page') || l.includes('panel'))                   return '🗂';
  if (l.includes('filter') || l.includes('search'))                return '🔍';
  if (l.includes('upload') || l.includes('import'))                return '⬆️';
  return '⬜';
}

/** Is this id a v4 GUID? Used to decide how to truncate it for display. */
function isGuid(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
}

// ── Module grouping ───────────────────────────────────────────────────────────
// The highlight tree used to group top-level nodes by their raw ROUTE URL, so
// "/logipulse/shipment-icon-grid-new", "/logipulse/shipment/shipmentDetails"
// and "/logipulse/job/jobDetails" each became a SEPARATE flat header. Agents
// couldn't tell that "Job Details" belongs under "Job". We now group by the
// MODULE (the first meaningful route segment) so everything rolls up:
//
//     📦 Job
//        └─ Job (list)
//        └─ Job Details
//             └─ tabs, buttons …
//
// Module is derived from the route segment after "/logipulse/", normalised so
// "shipment-icon-grid-new", "shipment", "shipmentDetails" all map to the same
// "shipment" bucket.

/** Map of route-segment stems → friendly module name + icon. */
const MODULE_META: Record<string, { name: string; icon: string }> = {
  shipment:   { name: 'Shipment',   icon: '📦' },
  job:        { name: 'Job',        icon: '🚚' },
  pickup:     { name: 'Pickup',     icon: '📥' },
  delivery:   { name: 'Delivery',   icon: '📤' },
  customs:    { name: 'Customs Clearance', icon: '🛃' },
  accounting: { name: 'Accounting', icon: '💰' },
  payment:    { name: 'Payment',    icon: '💳' },
  invoice:    { name: 'Invoice',    icon: '🧾' },
  customer:   { name: 'Customer',   icon: '🧑‍💼' },
  actor:      { name: 'Actors',     icon: '🧑‍💼' },
  sales:      { name: 'CRM / Sales',icon: '📈' },
  warehouse:  { name: 'Warehouse',  icon: '🏬' },
  quotation:  { name: 'Quotation',  icon: '📝' },
  voyage:     { name: 'Voyage',     icon: '🚢' },
  settings:   { name: 'Settings',   icon: '⚙️' },
  delivery_order: { name: 'Delivery', icon: '📤' },
  'message-dashboard': { name: 'Notifications', icon: '🔔' },
};

/**
 * Derive a stable module key from a route. Strips "/logipulse/", takes the
 * first path segment, drops common suffixes (-icon-grid-new, -menu-grid,
 * Details, -details) and camelCase tails so related routes collapse together.
 */
function moduleKeyFromRoute(route?: string): string {
  if (!route) return 'other';
  let seg = route.replace(/^\/+/, '');               // drop leading slash
  seg = seg.replace(/^logipulse\/+/i, '');           // drop /logipulse/
  seg = seg.split(/[/?#]/)[0] || '';                 // first path segment
  if (!seg) return 'other';
  seg = seg.toLowerCase();
  // Normalise: shipmentdetails → shipment, shipment-icon-grid-new → shipment
  seg = seg.replace(/[-_]?(icon[-_]?grid[-_]?new|menu[-_]?grid|grid[-_]?new|details?|new|list|create|edit)$/g, '');
  // Strip a camelCase tail that survived lowercasing (e.g. "shipmentdetails")
  for (const stem of Object.keys(MODULE_META)) {
    if (seg.startsWith(stem)) return stem;
  }
  return seg || 'other';
}

/** Friendly { name, icon } for a module key. */
function moduleMeta(key: string): { name: string; icon: string } {
  return MODULE_META[key] ?? {
    name: key === 'other' ? 'Other' : key.charAt(0).toUpperCase() + key.slice(1),
    icon: '📁',
  };
}

/** Compact display form for an id — first 8 chars for GUIDs, full for legacy. */
function shortId(id: string): string {
  return isGuid(id) ? id.slice(0, 8) + '…' : id;
}

// ── Export tree as text ───────────────────────────────────────────────────────
function treeToText(nodes: UiTreeNode[], prefix = ''): string {
  return nodes.map((node, idx) => {
    const last = idx === nodes.length - 1;
    const line = `${prefix}${last ? '└── ' : '├── '}${node.label} [${shortId(node.id)}]`;
    const next = prefix + (last ? '    ' : '│   ');
    const children = node.children.length ? '\n' + treeToText(node.children, next) : '';
    return line + children;
  }).join('\n');
}

// ── Tree Node ─────────────────────────────────────────────────────────────────
const TreeNode: React.FC<{
  node:     UiTreeNode;
  depth:    number;
  search:   string;
  onSelect: (id: string) => void;
  selected: string | null;
}> = ({ node, depth, search, onSelect, selected }) => {
  const [open, setOpen] = useState(depth < 2);
  const hasChildren = node.children.length > 0;

  const q = search.toLowerCase();
  const matchesSelf = q ? (
    node.id.toLowerCase().includes(q) ||
    node.label.toLowerCase().includes(q)
  ) : true;
  const childrenMatch = q ? node.children.some(c =>
    c.id.toLowerCase().includes(q) ||
    c.label.toLowerCase().includes(q)
  ) : true;

  if (search && !matchesSelf && !childrenMatch) return null;
  const isSelected = selected === node.id;

  return (
    <div className={s.nodeWrap}>
      <div
        className={`${s.node} ${isSelected ? s.nodeSelected : ''} ${!matchesSelf && search ? s.nodeDimmed : ''}`}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={() => onSelect(node.id)}
      >
        {hasChildren ? (
          <span className={s.toggle} onClick={e => { e.stopPropagation(); setOpen(p => !p); }}>
            {open ? '▾' : '▸'}
          </span>
        ) : (
          <span className={s.toggleSpacer} />
        )}
        <span className={s.typeIcon}>{typeIcon(node.elementType, node.label, node.id)}</span>
        <span className={s.label}>{node.label}</span>
        {node.elementType && (
          <span
            title={`Element type: ${node.elementType}`}
            className={`${s.typeBadge} ${isMainSection(node.elementType) ? s.typeBadgeMain : ''}`}>
            {node.elementType}
          </span>
        )}
        <span className={s.id} title={node.id}>{shortId(node.id)}</span>
        {node.route && <span className={s.route}>{node.route}</span>}
      </div>
      {hasChildren && open && (
        <div className={s.children}>
          {node.children.map(child => (
            <TreeNode key={child.id} node={child} depth={depth + 1}
              search={search} onSelect={onSelect} selected={selected} />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Module group (collapsible top-level bucket) ───────────────────────────────
// Wraps all of a module's nodes (list page, details page, sub-pages) under one
// friendly header — "📦 Shipment", "🚚 Job" — so the hierarchy reads naturally
// instead of being split across raw route URLs.
const ModuleGroup: React.FC<{
  moduleKey: string;
  nodes:     UiTreeNode[];
  search:    string;
  onSelect:  (id: string) => void;
  selected:  string | null;
}> = ({ moduleKey, nodes, search, onSelect, selected }) => {
  const [open, setOpen] = useState(true);
  const meta = moduleMeta(moduleKey);

  // Count every node in the subtree (for the header badge).
  const total = useMemo(() => {
    let n = 0;
    const walk = (list: UiTreeNode[]) => list.forEach(x => { n++; walk(x.children); });
    walk(nodes);
    return n;
  }, [nodes]);

  // When searching, hide the whole module if nothing inside matches.
  const q = search.toLowerCase();
  const anyMatch = useMemo(() => {
    if (!q) return true;
    const hit = (x: UiTreeNode): boolean =>
      x.id.toLowerCase().includes(q) ||
      x.label.toLowerCase().includes(q) ||
      x.children.some(hit);
    return nodes.some(hit);
  }, [nodes, q]);

  // Order roots within a module so it reads top-down: the nav item / list
  // landing page first, then details, then deeper sub-pages. Heuristic: rank
  // by element type (nav-item/menu first), then by route depth (shorter =
  // higher), then alphabetically by label.
  const orderedNodes = useMemo(() => {
    const rank = (n: UiTreeNode): number => {
      const t = (n.elementType ?? '').toLowerCase();
      if (t === 'nav-item' || t === 'menu') return 0;
      const r = (n.route ?? '').toLowerCase();
      if (/details?/.test(r)) return 3;          // details pages after list
      return /grid|list|icon-grid|menu-grid/.test(r) ? 1 : 2;
    };
    return [...nodes].sort((a, b) => {
      const ra = rank(a), rb = rank(b);
      if (ra !== rb) return ra - rb;
      const da = (a.route ?? '').split('/').length;
      const db = (b.route ?? '').split('/').length;
      if (da !== db) return da - db;
      return a.label.localeCompare(b.label);
    });
  }, [nodes]);

  // All hooks are above this line. Safe to bail out now: when a search filters
  // the module out entirely, render nothing.
  if (!anyMatch) return null;

  return (
    <div className={s.routeGroup}>
      <div className={s.routeHeader} onClick={() => setOpen(p => !p)}>
        <span className={s.routeChevron}>{open ? '▾' : '▸'}</span>
        <span>{meta.icon} {meta.name}</span>
        <span className={s.routeCount}>({total})</span>
      </div>
      {open && orderedNodes.map(node => (
        <TreeNode key={node.id} node={node} depth={1}
          search={search} onSelect={onSelect} selected={selected} />
      ))}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
export const UiTreeViewer: React.FC<{
  elements:         UiElement[];
  onSelectElement?: (id: string) => void;
  /**
   * Optional. If provided, a "Highlight" button appears next to a selected
   * element. Clicking it sends a single-element highlight sequence to the
   * user. The parent component (typically UserPanel) wires this to
   * sendHighlight(targetUserId, [id], intervalMs).
   */
  onHighlight?: (id: string, label: string) => void;
  /**
   * Optional. If provided, a "Send sequence to this element" button
   * appears for a selected element. The parent wires this to the SignalR
   * SendSequenceToElement call — the server walks the persisted tree to
   * build a multi-step guided flow from the topmost landing point down
   * to the target.
   */
  onSendSequence?: (id: string, label: string) => void;
  /**
   * Optional. If provided, a "Clear server tree" button appears in the
   * toolbar. The parent component wires this to a fetch() call against
   * POST /api/ui-tree/clear which wipes the server's in-memory registry,
   * deletes the persisted JSON file, and broadcasts an empty snapshot to
   * every connected support agent.
   */
  onClearServerTree?: () => Promise<void> | void;
  /**
   * Optional. If provided, clicking a tree node fires this callback with
   * the node's GUID and label. The Guidance panel uses this to append to
   * its sequence builder. When set, the per-element "Send sequence" and
   * "Highlight" action buttons HIDE — the click itself is the action,
   * because picking-to-build-a-sequence is the dominant gesture in
   * that mode.
   */
  onAddToSequence?: (id: string, label: string) => void;
}> = ({ elements, onSelectElement, onHighlight, onSendSequence, onClearServerTree, onAddToSequence }) => {
  const [search,   setSearch]   = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [copied,   setCopied]   = useState(false);
  const [highlighted, setHighlighted] = useState(false);

  // ── "Main sections only" toggle, persisted across sessions ───────────────
  // Default ON — the highlight tree is far less noisy when individual buttons
  // are hidden and only menus / tabs / panels / grids show. The agent flips
  // it off on the rare occasion they need to target a specific button.
  const [mainOnly, setMainOnly] = useState<boolean>(() => {
    try { return localStorage.getItem('lp_uitree_main_only') !== 'false'; }
    catch { return true; }
  });
  const toggleMainOnly = () => {
    setMainOnly(prev => {
      const next = !prev;
      try { localStorage.setItem('lp_uitree_main_only', String(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const fullTree = useMemo(() => buildTree(elements), [elements]);
  // When mainOnly is on, collapse detail nodes (buttons, menu items) but keep
  // main-section descendants reachable by lifting them to the nearest kept
  // ancestor. Mirrors the server's flow filter so tree and flows agree.
  const tree = useMemo(
    () => (mainOnly ? filterTreeToMainSections(fullTree) : fullTree),
    [fullTree, mainOnly],
  );
  const byId = useMemo(() => new Map(elements.map(e => [e.id, e])), [elements]);

  // Count of detail nodes hidden by the toggle — shown in the toolbar so the
  // agent knows the tree is condensed, not incomplete.
  const hiddenCount = useMemo(() => {
    if (!mainOnly) return 0;
    return elements.filter(e => !isMainSection(e.elementType)).length;
  }, [elements, mainOnly]);

  // Group top-level nodes by MODULE so related routes (a module's list page,
  // its details page, sub-pages) roll up under ONE header instead of being
  // scattered across separate route URLs. Modules are sorted alphabetically
  // by friendly name, with "Other" last.
  const moduleGroups = useMemo(() => {
    const groups: Record<string, UiTreeNode[]> = {};
    tree.forEach(node => {
      const key = moduleKeyFromRoute(node.route);
      (groups[key] ??= []).push(node);
    });
    // Stable, readable ordering.
    return Object.entries(groups).sort(([a], [b]) => {
      if (a === 'other') return 1;
      if (b === 'other') return -1;
      return moduleMeta(a).name.localeCompare(moduleMeta(b).name);
    });
  }, [tree]);

  const handleSelect = (id: string) => {
    setSelected(id);
    onSelectElement?.(id);
    // When the parent is in "build a sequence" mode, every click on a
    // tree node appends that node to the sequence. The user picks
    // landing-page → page-action → control sequentially.
    if (onAddToSequence) {
      const el = byId.get(id);
      onAddToSequence(id, el?.label || id);
    }
  };

  const handleHighlight = () => {
    if (!selected || !onHighlight) return;
    const el = byId.get(selected);
    onHighlight(selected, el?.label || selected);
    setHighlighted(true);
    setTimeout(() => setHighlighted(false), 2000);
  };

  const [sentSeq, setSentSeq] = useState(false);
  const handleSendSequence = () => {
    if (!selected || !onSendSequence) return;
    const el = byId.get(selected);
    onSendSequence(selected, el?.label || selected);
    setSentSeq(true);
    setTimeout(() => setSentSeq(false), 2000);
  };

  const exportText = () => {
    const text = 'Application\n' + treeToText(tree, ' ');
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const [clearing, setClearing] = useState(false);
  const handleClearServer = async () => {
    if (!onClearServerTree) return;
    if (!window.confirm(
      'Clear the server\'s UI tree?\n\n' +
      'This wipes the in-memory registry, deletes the persisted JSON ' +
      'file, and resets every connected support agent\'s view.\n\n' +
      'The tree will rebuild as Angular clients push fresh snapshots.',
    )) return;
    setClearing(true);
    try { await onClearServerTree(); }
    finally { setTimeout(() => setClearing(false), 1500); }
  };

  if (elements.length === 0) {
    return (
      <div className={s.empty}>
        <div className={s.emptyIco}>🌳</div>
        <div>No UI elements yet.</div>
        <div className={s.emptyHint}>The Angular app sends its element tree via WebSocket.</div>
      </div>
    );
  }

  const showGroups = moduleGroups.length > 1;
  const selectedEl = selected ? byId.get(selected) : null;

  return (
    <div className={s.viewer}>
      <div className={s.toolbar}>
        <input
          className={s.search}
          placeholder="Search by label or GUID…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className={s.stats}>
          {elements.length} elements{mainOnly && hiddenCount > 0 ? ` · ${hiddenCount} hidden` : ''}
        </div>
        <label
          className={s.mainToggle}
          title="Show only main sections (menus, tabs, panels, grids, context menus) and hide individual buttons and menu items. Turn off to target a specific button.">
          <input type="checkbox" checked={mainOnly} onChange={toggleMainOnly} />
          Main sections only
        </label>
        <button className={s.exportBtn} onClick={exportText}>
          {copied ? '✅ Copied' : '📋 Export'}
        </button>
        {onClearServerTree && (
          <button
            className={s.exportBtn}
            onClick={handleClearServer}
            disabled={clearing}
            title="Wipe the server's UI tree registry and broadcast the reset to every agent. The tree will rebuild as Angular clients push fresh snapshots.">
            {clearing ? '⏳ Clearing…' : '🗑️ Clear server tree'}
          </button>
        )}
      </div>

      <div className={s.tree}>
        <div className={s.treeRoot}>🌐 Application</div>
        {showGroups
          ? moduleGroups.map(([key, nodes]) => (
              <ModuleGroup key={key} moduleKey={key} nodes={nodes}
                search={search} onSelect={handleSelect} selected={selected} />
            ))
          : tree.map(node => (
              <TreeNode key={node.id} node={node} depth={1}
                search={search} onSelect={handleSelect} selected={selected} />
            ))
        }
      </div>

      {selected && selectedEl && (
        <div className={s.selectedInfo}>
          <div className={s.selectedLine}>
            <span className={s.selectedLabel}>Selected:</span>
            <span className={s.selectedName}>{selectedEl.label}</span>
          </div>
          <div className={s.selectedLine}>
            <span className={s.selectedSub}>id:</span>
            <code className={s.selectedId} title={selected}>{selected}</code>
            <button className={s.copyBtn} onClick={() => {
              navigator.clipboard.writeText(selected);
              setCopied(true); setTimeout(() => setCopied(false), 1500);
            }}>
              {copied ? '✅' : '📋 Copy ID'}
            </button>
            {!onAddToSequence && onHighlight && (
              <button
                className={s.highlightBtn}
                onClick={handleHighlight}
                title="Draw a red pulsing circle around this element on the user's screen"
              >
                {highlighted ? '✨ Sent' : '🎯 Highlight on user screen'}
              </button>
            )}
            {!onAddToSequence && onSendSequence && (
              <button
                className={s.seqBtn}
                onClick={handleSendSequence}
                title="Send a step-by-step guidance flow that walks the user from a landing page (side menu / top bar) down to this element. Each step highlights one item and waits for the user to click before advancing."
              >
                {sentSeq ? '✨ Sequence sent' : '🧭 Send sequence to this element'}
              </button>
            )}
            {onAddToSequence && (
              <span className={s.selectedSub} style={{ marginLeft: 8 }}>
                ✓ Added to sequence (click another node to add more)
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
